import React, {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTrackPlayback } from '../../../store/TrackPlaybackContext';
import './index.scss';

const getTrackTitle = (track) => {
  if (typeof track?.title === 'string' && track.title.trim()) {
    return track.title.trim();
  }
  if (typeof track?.originalFileName === 'string' && track.originalFileName.trim()) {
    return track.originalFileName.replace(/\.[^/.]+$/, '').trim();
  }
  if (typeof track?.objectKey === 'string' && track.objectKey.trim()) {
    return track.objectKey.split('/').pop()?.replace(/\.[^/.]+$/, '').trim() || 'Untitled track';
  }
  return 'Untitled track';
};

const getArtworkUrl = (track, sourceImage) => {
  const rawUrl = track?.cover || track?.artworkUrl || sourceImage || '';
  return rawUrl ? resolveMediaUrl(rawUrl) : null;
};

const PreviousIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M6 5h2v14H6zM10 12l9 7V5z" />
  </svg>
);

const NextIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 5h2v14h-2zM5 19l9-7-9-7z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 5h4v14H7zM13 5h4v14h-4z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const MinimizeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 12h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ExpandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M8 14l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function GlobalTrackMiniPlayer() {
  const {
    state,
    togglePlay,
    playNext,
    playPrevious,
    stopPlayback,
  } = useTrackPlayback();

  const {
    activeTrack,
    activeTrackId,
    activeSourceId,
    isPlaying,
    isLoading,
    progressBySourceId,
    queue,
    queueIndex,
    sourceImage,
    sourceLabel,
    sourceSubtitle,
  } = state;

  const portalRoot = typeof document !== 'undefined'
    ? (document.getElementById('portal') || document.body)
    : null;

  const [isMinimized, setIsMinimized] = useState(false);

  const progress = activeSourceId && activeTrackId
    ? progressBySourceId?.[activeSourceId]?.[activeTrackId] || 0
    : 0;
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const hasPrevious = queueIndex > 0;
  const hasNext = queueIndex >= 0 && queueIndex < queue.length - 1;
  const title = getTrackTitle(activeTrack);
  const artworkUrl = getArtworkUrl(activeTrack, sourceImage);
  const sourceText = sourceLabel || sourceSubtitle || 'Muzone';
  const trackMeta = typeof activeTrack?.meta === 'string' ? activeTrack.meta : '';
  const subText = sourceLabel && sourceSubtitle ? sourceSubtitle : trackMeta;

  const artworkStyle = useMemo(
    () => (artworkUrl ? { backgroundImage: `url("${artworkUrl}")` } : undefined),
    [artworkUrl],
  );

  const titleRef = useRef(null);
  const titleTextRef = useRef(null);
  const [marqueeShift, setMarqueeShift] = useState(0);

  useLayoutEffect(() => {
    const container = titleRef.current;
    const text = titleTextRef.current;
    if (!container || !text) {
      return undefined;
    }
    const measure = () => {
      const overflow = text.scrollWidth - container.clientWidth;
      setMarqueeShift(overflow > 4 ? overflow : 0);
    };
    measure();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(container);
    }
    return () => observer?.disconnect();
  }, [title]);

  // When minimized the player becomes a full-width bar pinned to the bottom of
  // the viewport. Reserve matching space on the scroll container (.app-shell)
  // via a body class so the bar never overlaps page content on any tab.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const shouldReserve = Boolean(isMinimized && activeTrack);
    document.body.classList.toggle('has-minimized-mini-player', shouldReserve);
    return () => {
      document.body.classList.remove('has-minimized-mini-player');
    };
  }, [isMinimized, activeTrack]);

  const isMarquee = marqueeShift > 0;
  const titleStyle = isMarquee
    ? {
      '--mini-marquee-shift': `${-marqueeShift}px`,
      '--mini-marquee-duration': `${Math.max(8, marqueeShift / 22 + 6)}s`,
    }
    : undefined;

  if (!portalRoot || !activeTrack) {
    return null;
  }

  if (isMinimized) {
    return ReactDOM.createPortal(
      <aside
        className="global-track-mini-player global-track-mini-player--minimized"
        aria-label="Current track (minimized)"
      >
        <div className="global-track-mini-player__bar-progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${clampedProgress})` }} />
        </div>
        <button
          type="button"
          className="global-track-mini-player__bar"
          onClick={() => setIsMinimized(false)}
          aria-label="Expand player"
          title="Expand player"
        >
          <span
            className={`global-track-mini-player__bar-art${artworkUrl ? '' : ' global-track-mini-player__bar-art--empty'}`}
            style={artworkStyle}
            aria-hidden="true"
          >
            {!artworkUrl ? (
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 18V5l11-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="17" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
              </svg>
            ) : null}
          </span>
          <span className="global-track-mini-player__bar-copy">
            <span className="global-track-mini-player__bar-title">{title}</span>
            <span className="global-track-mini-player__bar-source">{sourceText}</span>
          </span>
        </button>
        <div className="global-track-mini-player__bar-controls">
          <button
            type="button"
            className="global-track-mini-player__bar-button"
            aria-label="Previous track"
            onClick={playPrevious}
            disabled={!hasPrevious || isLoading}
          >
            <PreviousIcon />
          </button>
          <button
            type="button"
            className="global-track-mini-player__bar-button global-track-mini-player__bar-button--primary"
            aria-label={isPlaying ? 'Pause track' : 'Play track'}
            aria-pressed={isPlaying}
            onClick={togglePlay}
            disabled={isLoading}
          >
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>
          <button
            type="button"
            className="global-track-mini-player__bar-button"
            aria-label="Next track"
            onClick={playNext}
            disabled={!hasNext || isLoading}
          >
            <NextIcon />
          </button>
          <span className="global-track-mini-player__bar-divider" aria-hidden="true" />
          <button
            type="button"
            className="global-track-mini-player__bar-button"
            aria-label="Expand player"
            title="Expand player"
            onClick={() => setIsMinimized(false)}
          >
            <ExpandIcon />
          </button>
          <button
            type="button"
            className="global-track-mini-player__bar-button"
            aria-label="Close player and stop music"
            title="Close player and stop music"
            onClick={stopPlayback}
          >
            <CloseIcon />
          </button>
        </div>
      </aside>,
      portalRoot,
    );
  }

  return ReactDOM.createPortal(
    <aside className="global-track-mini-player" aria-label="Current track">
      <button
        type="button"
        className="global-track-mini-player__icon-button global-track-mini-player__minimize"
        aria-label="Minimize player"
        title="Minimize player"
        onClick={() => setIsMinimized(true)}
      >
        <MinimizeIcon />
      </button>
      <button
        type="button"
        className="global-track-mini-player__icon-button global-track-mini-player__close"
        aria-label="Close player and stop music"
        title="Close player and stop music"
        onClick={stopPlayback}
      >
        <CloseIcon />
      </button>
      <div
        className={`global-track-mini-player__art${artworkUrl ? '' : ' global-track-mini-player__art--empty'}`}
        style={artworkStyle}
        aria-hidden="true"
      >
        {!artworkUrl ? (
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 18V5l11-2v13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.8" />
            <circle cx="17" cy="16" r="3" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        ) : null}
      </div>
      <div className="global-track-mini-player__main">
        <div className="global-track-mini-player__copy">
          <div
            ref={titleRef}
            className={`global-track-mini-player__title${isMarquee ? ' global-track-mini-player__title--marquee' : ''}`}
            title={title}
            style={titleStyle}
          >
            <span ref={titleTextRef} className="global-track-mini-player__title-text">
              {title}
            </span>
          </div>
          <div className="global-track-mini-player__source" title={sourceText}>
            {sourceText}
          </div>
          {subText ? (
            <div className="global-track-mini-player__meta" title={subText}>
              {subText}
            </div>
          ) : null}
        </div>
        <div className="global-track-mini-player__progress" aria-hidden="true">
          <span style={{ transform: `scaleX(${clampedProgress})` }} />
        </div>
      </div>
      <div className="global-track-mini-player__controls">
        <button
          type="button"
          className="global-track-mini-player__button"
          aria-label="Previous track"
          onClick={playPrevious}
          disabled={!hasPrevious || isLoading}
        >
          <PreviousIcon />
        </button>
        <button
          type="button"
          className="global-track-mini-player__button global-track-mini-player__button--primary"
          aria-label={isPlaying ? 'Pause track' : 'Play track'}
          aria-pressed={isPlaying}
          onClick={togglePlay}
          disabled={isLoading}
        >
          {isPlaying ? <PauseIcon /> : <PlayIcon />}
        </button>
        <button
          type="button"
          className="global-track-mini-player__button"
          aria-label="Next track"
          onClick={playNext}
          disabled={!hasNext || isLoading}
        >
          <NextIcon />
        </button>
      </div>
    </aside>,
    portalRoot,
  );
}
